import { durationLabel } from 'utils/ru';
import { formatDate as localeDate } from 'i18n/format';
import React, { useId } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useDbtProject, useDbtRuns } from 'api/warehouse';
import { extractApiError } from 'api/errors';
import StatusBadge from 'components/common/StatusBadge';
import useRole from 'hooks/useRole';
import ReadinessTab from './ReadinessTab';
import PreparationRunButton from './PreparationRunButton';

import { useTranslation } from 'react-i18next';

function formatDate(iso) {
  if (!iso) return '—';
  return localeDate(new Date(iso), { dateStyle: 'medium', timeStyle: 'short' });
}

function formatDuration(start, end) {
  return durationLabel(start, end);
}

const RUN_STATUS_COLOR = {
  pending: 'default', running: 'info', success: 'success', failed: 'error', cancelled: 'warning',
};
const RUN_BADGE_STATUS = {
  pending: 'PENDING', running: 'STARTED', success: 'SUCCESS', failed: 'FAILURE', cancelled: 'REVOKED',
};

export default function DbtProjectDetailPage() {
  const { id } = useParams();
  return <PreparationWorkspace projectId={id} />;
}

/** Shared inline hub workspace and the saved-link detail route. */
export function PreparationWorkspace({ projectId, embedded = false }) {
  // Reset task-control state when another project is selected.
  return <PreparationWorkspaceContent key={projectId} projectId={projectId} embedded={embedded} />;
}

function PreparationWorkspaceContent({ projectId, embedded }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useRole();
  const historyId = useId();

  const { data: project, isLoading, error } = useDbtProject(projectId);

  const searchParams = new URLSearchParams(location.search);
  const historyOpen = searchParams.get('history') === 'open'
    || (!embedded && searchParams.get('tab') === 'runs');
  const changeHistory = (_, expanded) => {
    const next = new URLSearchParams(location.search);
    if (!embedded && next.get('tab') === 'runs') next.delete('tab');
    if (expanded) next.set('history', 'open');
    else next.delete('history');
    navigate({ pathname: location.pathname, search: next.toString() ? `?${next}` : '', hash: location.hash }, { replace: true });
  };

  const back = () => navigate('/console/pipelines?tab=preparation');

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress aria-label={t('data.workspacePreparation.loading', { defaultValue: 'Loading preparation' })} /></Box>;
  if (error || !project) return <Alert severity="error">{extractApiError(error, t('data.workspacePreparation.loadError', { defaultValue: 'Could not load preparation.' }))} {!embedded && <Button size="small" onClick={back}>{t('data.goBack')}</Button>}</Alert>;

  const history = (
    <Accordion expanded={historyOpen} onChange={changeHistory} disableGutters elevation={0} TransitionProps={{ unmountOnExit: true }} sx={{ border: '1px solid', borderColor: 'divider' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} id={`${historyId}-heading`} aria-controls={`${historyId}-content`}>
        <Box>
          <Typography variant="body2" fontWeight={600}>{t('data.runHistory')}</Typography>
          {project.last_run_at && <Typography variant="caption" color="text.secondary">{t('data.lastRun')}{' '}{formatDate(project.last_run_at)}</Typography>}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {historyOpen && <PreparationHistory projectId={projectId} isAdmin={isAdmin} />}
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Box>
      {/* Header */}
      {!embedded && <Button startIcon={<ArrowBackIcon />} onClick={back} size="small" color="inherit" sx={{ mb: 2 }}>
        {t('data.workspacePreparation.back', { defaultValue: 'All preparation' })}
      </Button>}
      <Box sx={{ display: 'grid', alignItems: 'start', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: isAdmin ? 'minmax(0, 1fr) 232px' : 'minmax(0, 1fr)' }, gap: 2, mb: 3 }}>
          <Box sx={{ minWidth: 0 }}>
            {!embedded && <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {t('data.workspacePreparation.title', { defaultValue: 'Data preparation' })}
            </Typography>}
            <Typography component={embedded ? 'h2' : 'h1'} variant="h5" fontWeight={700} sx={{ overflowWrap: 'anywhere' }}>{project.name}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              <StatusBadge status={RUN_BADGE_STATUS[project.last_run?.status] || project.status} />
              {project.managed && <Chip label={t('data.managedByPlatform')} size="small" variant="outlined" />}
              {!project.is_active && <Chip label={t('data.disabled')} size="small" color="warning" />}
            </Box>
          </Box>
        {isAdmin && (
          <Box sx={{ justifySelf: 'end', width: 232, maxWidth: '100%' }}>
            <PreparationRunButton projectId={projectId} disabled={!project.is_active || ['pending', 'running'].includes(project.last_run?.status)} />
          </Box>
        )}
      </Box>

      <ReadinessTab projectId={projectId}>{history}</ReadinessTab>

    </Box>
  );
}

/** Mount only while history is expanded; closing it stops the run-list poll. */
function PreparationHistory({ projectId, isAdmin }) {
  const { t } = useTranslation();
  const { data: runs, isLoading, error } = useDbtRuns(projectId);
  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} aria-label={t('data.workspacePreparation.historyLoading', { defaultValue: 'Loading run history' })} /></Box>;
  if (error) return <Alert severity="error">{extractApiError(error, t('data.workspacePreparation.historyError', { defaultValue: 'Could not load run history.' }))}</Alert>;
  if (!runs?.length) return (
    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>{isAdmin ? t('data.noRunsYetClickRunNowToTriggerTheFirstRun') : t('data.workspacePreparation.noRuns', { defaultValue: 'No preparation runs yet.' })}</Typography>
  );
  return (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell>{t('data.run2')}</TableCell>
                <TableCell>{t('data.command')}</TableCell>
                <TableCell>{t('data.status')}</TableCell>
                <TableCell>{t('data.trigger')}</TableCell>
                <TableCell>{t('data.started')}</TableCell>
                <TableCell>{t('data.duration')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.map((run) => (
                <TableRow hover key={run.public_id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}><Typography variant="caption" color="text.secondary">{run.name}</Typography></TableCell>
                    <TableCell><Typography variant="caption" fontFamily="monospace">{run.command}</Typography></TableCell>
                    <TableCell><Chip label={run.status} size="small" color={RUN_STATUS_COLOR[run.status] || 'default'} sx={{ textTransform: 'capitalize' }} /></TableCell>
                    <TableCell><Chip label={run.trigger} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} /></TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{formatDate(run.started_at || run.created_at)}</Typography></TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{formatDuration(run.started_at, run.finished_at)}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
  );
}
